import iconFacebookSvg from '@listening/assets/astro/icons/blog/icon-facebook.svg';
import iconInstagramSvg from '@listening/assets/astro/icons/blog/icon-instagram.svg';
import type { PropsWithChildren } from 'react';
import { ButtonSocial } from '@/components/astro/blog/button-social';
import { FormattedDate } from '@/components/astro/blog/FormattedDate';
import { NavBarAndProgressIndicator } from '@/components/astro/blog/nav-bar-and-progress-indicator';
import { Tag } from '@/components/astro/blog/tag';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { buildSlug } from '@listening/utils/slug-utils';
import type { BlogPostSummaryType, PublicDocPromotionType } from './blog-types';
import { MoreArticlesSection } from './more-articles-section';

type Props = {
  post: BlogPostSummaryType;
  randomPosts: ReadonlyArray<BlogPostSummaryType>;
  randomDocs: ReadonlyArray<PublicDocPromotionType>;
} & PropsWithChildren;

export function BlogPost(props: Props) {
  const {
    post: {
      title,
      excerpt,
      pubDate,
      heroImage,
      authorImage,
      author,
      tags,
      heroImageAlt,
    },
    randomPosts,
    children,
    randomDocs,
  } = props;

  // const posts = (
  //   await wordpressGraphqlService.getAllPostsWithSlugs(
  //     VITE_WORDPRESS_GRAPHQL_URL,
  //   )
  // ).map((node) => ({
  //   slug: node.slug,
  //   data: wordpressGraphqlService.convertToLegacyFormat(node),
  // }));

  // const chooseRandom = (() => {
  //   const rng = seedrandom(title);

  //   return <T,>(arr: T[]): T => {
  //     const rand = rng.quick();
  //     const choice = arr[Math.floor(rand * arr.length)];
  //     if (!choice) {
  //       throw new Error(
  //         `No choice found in array. Array length: ${arr.length.toString()} Random number: ${rand.toString()}`,
  //       );
  //     }
  //     return choice;
  //   };
  // })();

  // const randomPosts = [
  //   chooseRandom(posts),
  //   chooseRandom(posts),
  //   chooseRandom(posts),
  //   chooseRandom(posts),
  // ];

  // const docs = assertDefined(publicPdfDocMetadata[env.mode]).filter(
  //   (d) => d.audioConversion.cover_img,
  // );

  // const randomDocs =
  //   docs.length > 4
  //     ? [
  //         chooseRandom(docs),
  //         chooseRandom(docs),
  //         chooseRandom(docs),
  //         chooseRandom(docs),
  //       ]
  //     : [];

  return (
    <>
      <NavBarAndProgressIndicator />
      <main>
        <div className='mx-auto mt-4 mb-18 w-full max-w-[878px] px-4 sm:mt-20 sm:mb-28'>
          <div className='flex flex-col gap-8'>
            <h1 className='text-[2.75rem] leading-16.5 font-semibold text-neutral-900'>
              {title}
            </h1>
            <div
              className='font-["Poppins"] text-lg leading-loose font-normal text-neutral-500'
              dangerouslySetInnerHTML={{ __html: excerpt }}
            />
            <div className='flex flex-wrap items-center justify-between gap-8 border-y border-neutral-100 py-5'>
              <div className='flex gap-4'>
                <img
                  alt={author}
                  className='size-11 rounded-full object-cover'
                  height={44}
                  src={authorImage ?? undefined}
                  width={44}
                />
                <div className='flex flex-col gap-2'>
                  <p className='text-base leading-none font-medium text-black'>
                    {author}
                  </p>
                  <p className='text-sm leading-[14px] text-neutral-500'>
                    <FormattedDate date={pubDate} />
                  </p>
                </div>
              </div>

              <div className='flex flex-wrap items-center gap-6'>
                <ButtonSocial
                  alt='facebook listening.com'
                  href='https://www.facebook.com/listeningapp/'
                  src={iconFacebookSvg}
                />
                <ButtonSocial
                  alt='instagram listening.com'
                  href='https://www.instagram.com/listening/'
                  src={iconInstagramSvg}
                />
              </div>
            </div>

            <div>
              {heroImage && (
                <img
                  alt={heroImageAlt ?? undefined}
                  height={500}
                  src={heroImage}
                  width={878}
                />
              )}
            </div>
          </div>
          <div className='py-8'>{children}</div>

          <hr className='my-10 border-neutral-200 sm:my-14' />

          <div className='flex flex-wrap items-center justify-center gap-4'>
            <div className='flex flex-wrap items-center gap-3'>
              {tags.map((tag, index) => (
                <Tag key={`${tag}-${index.toString()}`} tagName={tag} />
              ))}
            </div>
          </div>
        </div>
      </main>

      <MoreArticlesSection
        cards={randomPosts.map((p) => ({
          href: normalizeCanonicalUrl(`/blog/${p.slug}`),
          author: p.author,
          authorImage: p.authorImage,
          heroImage: p.heroImage,
          heroImageAlt: p.heroImageAlt,
          pubDate: p.pubDate,
          title: p.title,
          description: p.description,
          tags: p.tags,
        }))}
        label='Recent  Articles'
      />

      <MoreArticlesSection
        cards={randomDocs.map((d) => ({
          author: d.metadata.authors,
          heroImage: d.audioConversion.cover_img ?? undefined,
          heroImageAlt: d.metadata.title,
          // publication_date is a raw string from the static catalog (often
          // an Excel serial like '40386'). FormattedDate handles invalid
          // values by rendering an empty <time> — same behavior as the
          // previous date-fns parse, which also produced Invalid Date for
          // these values.
          pubDate: d.metadata.publication_date,
          href: normalizeCanonicalUrl(
            `/doc/${buildSlug(d.metadata.title, d.audioConversion.id)}`,
          ),
          title: d.metadata.title,
          tags: d.metadata.category ? [d.metadata.category] : undefined,
        }))}
        label='Public  Documents'
      />

      <Footer />
    </>
  );
}
